import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Provisioning } from '@wsphere/warranties/domain';
import { Subject, firstValueFrom, map, takeUntil, tap } from 'rxjs';
import { ProvisioningSessionService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-provision-addon',
  templateUrl: './provision-addon.component.html',
  standalone: false,
})
export class ProvisionAddonComponent implements OnInit, OnDestroy {
  @HostBinding('class') _class = 'block max-w-full pb-24 px-6';

  private sessionSvc = inject(ProvisioningSessionService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private breakpointObserver = inject(BreakpointObserver);
  private destroy$ = new Subject<void>();

  protected addon$ = this.sessionSvc.addons$.asObservable();

  isLargeScreen$ = this.breakpointObserver
    .observe([Breakpoints.XSmall, Breakpoints.Small])
    .pipe(map(state => !state.matches));

  protected session$ = this.sessionSvc.getCurrentSession().pipe(
    tap(session => {
      if (session?.addonIds) {
        // Use emitEvent: false to avoid triggering updateSession on session refresh
        this.addon.patchValue(session.addonIds, { emitEvent: false });
      }
    })
  );

  protected addon = new FormControl(null as string[] | null);

  readonly staffView$ = this.route.data.pipe(map(data => data['staffView'] as boolean));

  ngOnInit(): void {
    // Update the session (and therefore the price preview) on every addon selection change
    this.addon.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(async addonIds => {
      const session = await firstValueFrom(this.sessionSvc.getCurrentSession());
      if (!session) return;
      await this.sessionSvc.updateSession({
        sessionId: session.id,
        data: { addonIds: addonIds ?? [] },
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private navigateTo(path?: string) {
    this.router.navigate([path ?? `..`], {
      relativeTo: this.route,
    });
  }

  previousPage(staffView: boolean) {
    if (staffView) this.navigateTo('/provisioning/plan');
    else this.navigateTo('/checkout/plan');
  }

  async nextPage(session: Provisioning.ProvisioningSession | null, staffView: boolean) {
    if (session && this.addon.value) {
      await this.sessionSvc.updateSession({
        sessionId: session.id,
        data: { addonIds: this.addon.value },
      });
    }
    if (staffView) this.navigateTo('/provisioning/checkout');
    else this.navigateTo('/checkout/checkout');
  }
}
